import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useToast } from '../components/contexts/ToastContext';
import useDebounce from './useDebounce';
import usePagination from './usePagination';

/**
 * Hook personalizado para manejar lógica CRUD paginada de entidades.
 * Encapsula toda la lógica repetitiva de fetch, paginación, filtros, modal y confirmación.
 *
 * Usa `usePagination` internamente. El modo de paginación se controla con `mode`:
 * - `'server'` (default): fetch por página, el servidor filtra y pagina
 * - `'client'`: carga todos los datos de una vez, pagina y filtra localmente
 *
 * @param {Object}   config
 * @param {string}   config.entityName         - Nombre singular de la entidad (ej: 'categoria')
 * @param {string}   config.entityNamePlural   - Nombre plural (ej: 'categorias')
 * @param {Function} config.fetchFn            - Función async para obtener datos: (params) => { data, pagination }
 * @param {Function} config.createFn           - Función async para crear: (formData) => { success, message }
 * @param {Function} config.updateFn           - Función async para actualizar: (id, formData) => { success, message }
 * @param {Function} config.deleteFn           - Función async para eliminar: (id) => { success, message }
 * @param {Object}   [config.defaultFilters]   - Valores iniciales de filtros. Default: { search: '' }
 * @param {string[]} [config.filterKeys]       - Claves de filtros a incluir en params de fetch. Default: ['search']
 * @param {number}   [config.pageSize=20]      - Items por página
 * @param {number}   [config.debounceDelay=300] - Delay en ms para debounce de búsqueda
 * @param {Object}   [config.messages]         - Mensajes personalizados para notificaciones
 * @param {'client'|'server'} [config.mode='client'] - Modo de paginación
 *
 * @returns {Object} Estado y handlers para la entidad
 */
function usePaginatedEntity({
  entityName,
  entityNamePlural,
  fetchFn,
  createFn,
  updateFn,
  deleteFn,
  defaultFilters = { search: '' },
  filterKeys = ['search'],
  pageSize = 20,
  debounceDelay = 300,
  messages = {},
  mode = 'client',
  onCreateSuccess,
}) {
  const { showToast } = useToast();
  const onCreateSuccessRef = useRef(onCreateSuccess);
  onCreateSuccessRef.current = onCreateSuccess;

  const msgs = {
    loadError:     `Error al cargar ${entityNamePlural}`,
    createSuccess: `${entityName.charAt(0).toUpperCase() + entityName.slice(1)} creado exitosamente`,
    updateSuccess: `${entityName.charAt(0).toUpperCase() + entityName.slice(1)} actualizado exitosamente`,
    deleteSuccess: `${entityName.charAt(0).toUpperCase() + entityName.slice(1)} eliminado exitosamente`,
    deleteError:   `Error al eliminar ${entityName}`,
    saveError:     `Error al guardar ${entityName}`,
    ...messages
  };

  // rawData: datos crudos del servidor (toda la página en server-mode, todos los registros en client-mode)
  const [rawData, setRawData]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filters, setFilters]     = useState(defaultFilters);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem]   = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, item: null });
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const debouncedSearch = useDebounce(filters.search || '', debounceDelay);

  const sortedData = useMemo(() => {
    if (!sortField || mode !== 'client') return rawData;
    return [...rawData].sort((a, b) => {
      const valA = a[sortField] ?? '';
      const valB = b[sortField] ?? '';
      let cmp;
      if (typeof valA === 'number' && typeof valB === 'number') {
        cmp = valA - valB;
      } else {
        cmp = String(valA).localeCompare(String(valB), 'es', { numeric: true });
      }
      return sortOrder === 'desc' ? -cmp : cmp;
    });
  }, [rawData, sortField, sortOrder, mode]);

  const {
    page, limit, totalItems,
    paginatedData,
    setTotal, handlePageChange, handleLimitChange, resetPage,
  } = usePagination({ mode, data: sortedData, initialLimit: pageSize });

  // data expuesto: slice local en client-mode, página del servidor en server-mode
  const data = mode === 'client' ? paginatedData : rawData;

  // Serializar filtros no-search para evitar recreación en cada render
  const filtersString = JSON.stringify(
    filterKeys
      .filter(k => k !== 'search')
      .reduce((acc, k) => {
        if (filters[k] !== undefined && filters[k] !== '') acc[k] = filters[k];
        return acc;
      }, {})
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search: debouncedSearch };

      filterKeys.forEach(key => {
        if (key !== 'search' && filters[key] !== undefined && filters[key] !== '') {
          params[key] = filters[key];
        }
      });

      if (mode === 'server') {
        params.page  = page;
        params.limit = limit;
      } else {
        params.limit = 1000;
      }

      const response = await fetchFn(params);
      if (response.success !== false) {
        setRawData(response.data || []);
        if (mode === 'server') setTotal(response.pagination?.total || 0);
      } else {
        showToast(response.message || msgs.loadError, 'error');
      }
    } catch (error) {
      showToast(msgs.loadError, 'error');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mode es estable; page/limit solo importan en server-mode
  }, mode === 'server'
    ? [page, limit, debouncedSearch, filtersString]
    : [debouncedSearch, filtersString]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = useCallback((columnKey) => {
    setSortField(prev => {
      if (prev === columnKey) {
        setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
      } else {
        setSortOrder('asc');
      }
      return columnKey;
    });
    resetPage();
  }, [resetPage]);

  const handleFilterChange = useCallback((name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    resetPage();
  }, [resetPage]);

  const handleCreate = useCallback(() => {
    setEditingItem(null);
    setModalOpen(true);
  }, []);

  const handleEdit = useCallback((item) => {
    setEditingItem(item);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingItem(null);
  }, []);

  const handleDelete = useCallback((item) => {
    setConfirmDialog({ open: true, item });
  }, []);

  const confirmDelete = useCallback(async () => {
    try {
      const response = await deleteFn(confirmDialog.item.id);
      if (response.success) {
        if (response.offline) {
          showToast('Operación guardada offline — se sincronizará al reconectar', 'warning');
          setConfirmDialog({ open: false, item: null });
          return;
        }
        showToast(msgs.deleteSuccess, 'success');
        fetchData();
      } else {
        showToast(response.message || msgs.deleteError, 'error');
      }
    } catch (error) {
      showToast(msgs.deleteError, 'error');
    } finally {
      setConfirmDialog({ open: false, item: null });
    }
  }, [confirmDialog.item, deleteFn, fetchData, showToast, msgs.deleteSuccess, msgs.deleteError]);

  const closeConfirm = useCallback(() => {
    setConfirmDialog({ open: false, item: null });
  }, []);

  const handleSave = useCallback(async (formData) => {
    try {
      const response = editingItem
        ? await updateFn(editingItem.id, formData)
        : await createFn(formData);

      if (response.success) {
        if (response.offline) {
          showToast('Operación guardada offline — se sincronizará al reconectar', 'warning');
          closeModal();
          return;
        }
        showToast(editingItem ? msgs.updateSuccess : msgs.createSuccess, 'success');
        if (!editingItem && onCreateSuccessRef.current) onCreateSuccessRef.current(response.data);
        closeModal();
        fetchData();
      } else {
        showToast(response.message || msgs.saveError, 'error');
      }
    } catch (error) {
      showToast(msgs.saveError, 'error');
    }
  }, [editingItem, createFn, updateFn, fetchData, showToast, msgs.createSuccess, msgs.updateSuccess, msgs.saveError, closeModal]);

  return {
    data,
    rawData,
    loading,

    // Forma { page, limit, total } preservada para compatibilidad con todas las páginas existentes
    pagination: { page, limit, total: totalItems },
    handlePageChange,
    handleLimitChange,

    filters,
    handleFilterChange,
    debouncedSearch,

    modalOpen,
    editingItem,
    handleCreate,
    handleEdit,
    closeModal,

    confirmDialog,
    handleDelete,
    confirmDelete,
    closeConfirm,

    handleSave,
    refetch: fetchData,

    sortField,
    sortOrder,
    handleSort,
  };
}

export default usePaginatedEntity;
